import { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";import ScoreGauge from "@/components/ScoreGauge";
import { StatusBadge, LevelBadge } from "@/components/StatusBadge";
import {
  Users, FolderKanban, Wallet, Bug, ListChecks, Clock, CheckCircle2,
  TrendingUp, AlertTriangle, DollarSign
} from "lucide-react";
import PendingInvitesBanner from "@/components/PendingInvitesBanner";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend
} from "recharts";

function KPI({ icon: Icon, label, value, hint, color = "text-cyan-600", testid }) {
  return (
    <div className="kpi-card" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">{label}</div>
        <Icon className={`w-4 h-4 ${color}`} strokeWidth={1.75} />
      </div>
      <div className="font-display text-3xl font-bold text-slate-900 mt-2">{value}</div>
      {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [pending, setPending] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    if (!user) return;
    (async () => {
      const [{ data: s }, { data: t }] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/tasks").catch(() => ({ data: [] })),
      ]);
      setStats(s); setTasks(t || []);
      if (user.role === "admin") {
        const r = await api.get("/users", { params: { role: "freelancer", status: "pending" } }).catch(() => ({data:[]}));
        setPending(r.data || []);
      }
      if (user.role === "manager") {
        const r = await api.get("/tasks", { params: { status: "Submitted" } }).catch(() => ({data:[]}));
        setPending(r.data || []);
      }
    })();
  }, [user]);

  if (!stats) return <AppLayout title="Loading..."><div className="text-slate-500">Loading dashboard…</div></AppLayout>;

  const greeting = user.role === "admin" ? "Operations Control" : user.role === "manager" ? "Manager Workspace" : "My Workspace";

  return (
    <AppLayout title={`Welcome, ${user.name.split(" ")[0]}`} subtitle={greeting}>
      <PendingInvitesBanner />
      {user.role === "admin" && <AdminDash stats={stats} pending={pending} nav={nav} />}
      {user.role === "manager" && <ManagerDash stats={stats} pending={pending} nav={nav} />}
      {user.role === "client" && <ClientDash stats={stats} nav={nav} />}
      {user.role === "freelancer" && <FreelancerDash stats={stats} tasks={tasks} nav={nav} />}
    </AppLayout>
  );
}

function ClientDash({ stats, nav }) {
  return (
    <div className="space-y-6" data-testid="client-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI icon={FolderKanban} label="Active projects" value={`${stats.active_projects}/${stats.total_projects}`} testid="kpi-active-projects" />
        <KPI icon={ListChecks} label="Open tasks" value={stats.open_tasks} testid="kpi-open-tasks" />
        <KPI icon={CheckCircle2} label="Completed" value={stats.completed_tasks} color="text-emerald-600" testid="kpi-completed-tasks" />
        <KPI icon={Bug} label="Valid issues" value={`${stats.valid_issues}/${stats.issues_total}`} color="text-amber-600" testid="kpi-valid-issues" />
      </div>
      <div className="kpi-card">
        <h3 className="font-display font-bold text-slate-900 mb-1">Your testing engagements</h3>
        <p className="text-sm text-slate-600 mb-3">You have read-only visibility into projects assigned to your organisation. Open a project to inspect tasks, issues and progress.</p>
        <Button onClick={()=>nav("/projects")} className="bg-slate-900 hover:bg-slate-800" data-testid="btn-client-view-projects">View my projects</Button>
      </div>
    </div>
  );
}

function AdminDash({ stats, pending, nav }) {
  const dist = Object.entries(stats.task_distribution || {}).map(([k, v]) => ({ name: k, value: v }));
  const COLORS = ["#94A3B8", "#3B82F6", "#F59E0B", "#8B5CF6", "#10B981", "#EF4444", "#0891B2"];
  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI icon={Users} label="Total users" value={stats.total_users} testid="kpi-total-users" />
        <KPI icon={FolderKanban} label="Active projects" value={stats.active_projects} testid="kpi-active-projects" />
        <KPI icon={DollarSign} label="Payments due" value={`$${stats.payments_due_amount.toLocaleString()}`} hint={`${stats.payments_due_count} pending`} testid="kpi-payments-due" />
        <KPI icon={CheckCircle2} label="Valid issues" value={`${stats.valid_issues}/${stats.total_issues}`} color="text-emerald-600" testid="kpi-valid-issues" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 kpi-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-slate-900">Task pipeline</h3>
            <div className="text-xs text-slate-500">Live</div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={dist}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="name" stroke="#64748B" fontSize={11} />
              <YAxis stroke="#64748B" fontSize={11} />
              <Tooltip />
              <Bar dataKey="value" fill="#0891B2" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="kpi-card">
          <h3 className="font-display font-bold text-slate-900 mb-2">Pending tester signups</h3>
          <div className="text-3xl font-display font-bold text-amber-600 mb-3">{stats.pending_signups}</div>
          {pending.slice(0,5).map(p => (
            <div key={p.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
              <div className="text-sm">
                <div className="text-slate-900 font-medium">{p.name}</div>
                <div className="text-slate-500 text-xs">{p.email}</div>
              </div>
              <Button size="sm" variant="outline" onClick={()=>nav(`/users/${p.id}`)} data-testid={`btn-review-${p.id}`}>Review</Button>
            </div>
          ))}
          {pending.length === 0 && <div className="text-sm text-slate-500">No pending signups.</div>}
        </div>
      </div>
    </div>
  );
}

function ManagerDash({ stats, pending, nav }) {
  return (
    <div className="space-y-6" data-testid="manager-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI icon={FolderKanban} label="Active projects" value={`${stats.active_projects}/${stats.total_projects}`} testid="kpi-active-projects" />
        <KPI icon={Clock} label="Pending approvals" value={stats.pending_approvals} color="text-amber-600" testid="kpi-pending-approvals" />
        <KPI icon={ListChecks} label="Open tasks" value={stats.open_tasks} testid="kpi-open-tasks" />
        <KPI icon={CheckCircle2} label="Completed" value={stats.completed_tasks} color="text-emerald-600" testid="kpi-completed-tasks" />
      </div>

      <div className="kpi-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-slate-900">Submissions awaiting your review</h3>
          <Button size="sm" variant="ghost" onClick={()=>nav("/tasks?status=Submitted")} data-testid="btn-view-all-submissions">View all</Button>
        </div>
        {pending.length === 0 && <div className="text-sm text-slate-500">No submissions pending. </div>}
        <div className="space-y-2">
          {pending.slice(0,8).map(t => (
            <div key={t.id} className="flex items-center justify-between p-3 border border-slate-200 rounded-md hover:border-cyan-300 cursor-pointer" onClick={()=>nav(`/tasks/${t.id}`)} data-testid={`row-pending-${t.id}`}>
              <div>
                <div className="font-medium text-slate-900 text-sm">{t.title}</div>
                <div className="text-xs text-slate-500">{t.project_name} · by {t.assignee_name || "—"}</div>
              </div>
              <StatusBadge kind="task" value={t.status} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function FreelancerDash({ stats, tasks, nav }) {
  const myTasks = tasks.slice(0, 6);
  return (
    <div className="space-y-6" data-testid="freelancer-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI icon={ListChecks} label="Open tasks" value={stats.open_tasks} testid="kpi-open-tasks" />
        <KPI icon={CheckCircle2} label="Accepted" value={stats.accepted_tasks} color="text-emerald-600" testid="kpi-accepted-tasks" />
        <KPI icon={DollarSign} label="Earnings (paid)" value={`$${stats.earnings_paid.toLocaleString()}`} hint={`$${stats.earnings_pending.toLocaleString()} pending`} testid="kpi-earnings" />
        <KPI icon={Bug} label="Valid issues" value={`${stats.valid_issues}/${stats.total_issues}`} testid="kpi-issues" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="kpi-card flex flex-col items-center justify-center py-8">
          <ScoreGauge score={stats.tester_score} tier={stats.tester_tier || stats.tester_level} state={stats.tester_state} />
          <button onClick={()=>nav("/score")} className="mt-3 text-xs text-cyan-700 hover:underline" data-testid="link-view-score">View score breakdown →</button>
        </div>
        <div className="lg:col-span-2 kpi-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display font-bold text-slate-900">My tasks</h3>
            <Button size="sm" variant="ghost" onClick={()=>nav("/tasks")} data-testid="btn-view-all-tasks">View all</Button>
          </div>
          {myTasks.length === 0 && <div className="text-sm text-slate-500">No tasks yet.</div>}
          <div className="space-y-2">
            {myTasks.map(t => (
              <div key={t.id} className="flex items-center justify-between p-3 border border-slate-200 rounded-md hover:border-cyan-300 cursor-pointer" onClick={()=>nav(`/tasks/${t.id}`)} data-testid={`row-task-${t.id}`}>
                <div>
                  <div className="font-medium text-slate-900 text-sm">{t.title}</div>
                  <div className="text-xs text-slate-500">{t.project_name} · ${t.payout_amount}</div>
                </div>
                <StatusBadge kind="task" value={t.status} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
